<template>
  <div
    class="resources"
    style="height: 100%;">
    <gdlDataTable
      ref="gdlTable"
      :columns="config.table.columns"
      :columns-order="columnsOrder"
      :data="resources.all"
      :loading="resources.pending"
      :pagination="pagination"
      :sort="resources.sort"
      :filters="resources.filters"
      :show-deleted="resources.filters.deleted"

      :enable-new-button="config.creatable"
      :editable="config.editable"
      :deletable="config.deletable"
      :printable="config.printable"

      style="height: 100%; width: 100%;"
      @columns-order-change="onColumnsOrderChange"
      @sort-change="onSortChange"
      @filter-change="onFilterChange"
      @pagination-change="onPaginationChange"
      @menu-action="onMenuAction"
      @batch-action="onBatchAction"
      @show-deleted-change="onShowDeletedChange"
      @createButton="goCreate()"/>

    <ConfirmationModal ref="confirmationModal" />
  </div>
</template>

<script>
import { get, isNil, isEqual, omitBy, defaults } from 'lodash';

import pageComponent from '@/mixins/page-component';
import gdlDataTable from '@/components/gdl/DataTable.vue';
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue';

import routerFilter from './mixins/route-filter';

export default {
  name: 'RestResourcesIndexPage',
  components: {
    gdlDataTable,
    ConfirmationModal,
  },
  mixins: [pageComponent, routerFilter],
  inject: ['config'],
  data() {
    const { defaultOrder } = this.config.table;
    return {
      columnsOrder: defaultOrder,
    };
  },
  computed: {
    name() { return this.config.route.name; },
    namespace() { return `${this.config.store.namespace}/Index`; },
    resources() { return get(this.$store.state, this.namespace.split('/')); },

    pagination() {
      const { pages, count } = this.resources.meta;
      return { ...this.resources.pagination, pages, count };
    },
  },
  created() {
    this.setDefaultsConfigs();

    const unwatch = this.$watch(
      vm => ((vm.resources.pagination, vm.resources.sort, vm.resources.filters)),
      this.fetchData,
      { immediate: true },
    );

    this.$once('hook:beforeDestroy', () => {
      unwatch();
    });
  },
  methods: {
    async fetchResources() { return this.$store.dispatch(`${this.namespace}/fetchResources`); },
    async deleteResource(id) { return this.$store.dispatch(`${this.namespace}/deleteResource`, { id }); },
    async restoreResource(id) { return this.$store.dispatch(`${this.namespace}/restoreResource`, { id }); },
    async deleteResources(ids) { return this.$store.dispatch(`${this.namespace}/deleteResources`, { ids }); },
    async restoreResources(ids) { return this.$store.dispatch(`${this.namespace}/restoreResources`, { ids }); },
    setPagination(pagination) { this.$store.dispatch(`${this.namespace}/setPagination`, pagination); },
    setSort(sort) { this.$store.dispatch(`${this.namespace}/setSort`, sort); },
    setFilters(filters) { this.$store.dispatch(`${this.namespace}/setFilters`, filters); },
    setDefaultsConfigs() {
      const {
        defaultPerPage: per = 250,
        perOptions = [5, 10, 20, 50, 100, 250, 500, 1000],
        defaultSort = [],
      } = this.config.table;
      const pagination = defaults({}, this.resources.pagination, { page: 1, per, perOptions });

      if (!isEqual(pagination, this.resources.pagination)) this.setPagination(pagination);
      if (!this.resources.sort) this.setSort(defaultSort);
    },

    async fetchData() { await this.fetchResources(); },
    async delete(resource) {
      const { key, i18n } = this.config.route;
      const label = get(resource, key);
      const table = this.$t(`${i18n}.meta.index.title`);
      const confirmed = await this.$refs.confirmationModal.open(
        'delete_confirmation',
        () => this.deleteResource(resource.id),
        { type: 'warning', label, table },

      );
      if (confirmed) this.fetchResources();
    },
    async restore(resource) {
      const { key, i18n } = this.config.route;
      const label = get(resource, key);
      const table = this.$t(`${i18n}.meta.index.title`);
      const confirmed = await this.$refs.confirmationModal.open(
        'restore_confirmation',
        () => this.restoreResource(resource.id),
        { label, table },
      );
      if (confirmed) this.fetchResources();
    },
    async deleteAll(ids) {
      const { i18n } = this.config.route;
      const table = this.$t(`${i18n}.meta.index.title`);
      const confirmed = await this.$refs.confirmationModal.open(
        'delete_all_confirmation',
        () => this.deleteResources(ids),
        { type: 'warning', count: ids.length, table },
      );
      if (confirmed) this.fetchResources();
      return confirmed;
    },
    async restoreAll(ids) {
      const { i18n } = this.config.route;
      const table = this.$t(`${i18n}.meta.index.title`);
      const confirmed = await this.$refs.confirmationModal.open(
        'restore_all_confirmation',
        () => this.restoreResources(ids),
        { count: ids.length, table },
      );
      if (confirmed) this.fetchResources();
      return confirmed;
    },
    async printAll(ids) {
      const { href } = this.$router.resolve({ name: 'print-raw-material', params: { id: ids.join('|') } });
      const { pathname } = global.window.location;
      global.window.open(
        `${pathname}${href}`,
        'Print',
        'scrollbars',
      );
      return true;
    },

    onPaginationChange(pagination) { this.setPagination(pagination); },
    onSortChange(sort) { this.setSort(sort); },
    onFilterChange({ column: { key }, value }) { this.setFilters({ [key]: value }); },
    onColumnsOrderChange(columnsOrder) { this.columnsOrder = columnsOrder; },
    onMenuAction({ action, selected }) {
      switch (action) {
        case 'show':
          this.goShow(selected);
          break;
        case 'edit':
          this.goEdit(selected);
          break;
        case 'delete':
          this.delete(selected);
          break;
        case 'restore':
          this.restore(selected);
          break;
        default:
      }
    },
    async onBatchAction({ action, selected }) {
      if (!selected.length) return;
      const ids = selected.map($ => $.id);
      switch (action) {
        case 'print-tag':
          if (await this.printAll(ids)) { this.$refs.gdlTable.clearbatchAction(); }
          break;
        case 'delete':
          if (await this.deleteAll(ids)) { this.$refs.gdlTable.clearbatchAction(); }
          break;
        case 'restore':
          if (await this.restoreAll(ids)) { this.$refs.gdlTable.clearbatchAction(); }
          break;
        default:
      }
    },
    onShowDeletedChange(deleted) {
      const query = omitBy({
        ...this.$route.query,
        _deleted: deleted ? true : null,
      }, isNil);
      this.$router.push({ ...this.$route, query });
    },

    goCreate() {
      this.$router.push({ name: `${this.name}-create` });
    },
    goShow({ id }) {
      this.$router.push({ name: `${this.name}-id`, params: { id } });
    },
    goEdit({ id }) {
      this.$router.push({ name: `${this.name}-id-edit`, params: { id } });
    },
  },
};
</script>

<style lang="scss" module>
</style>
